import { Room, RoomType, ViewType, RoomAmenity, RoomImage } from '@/types/room';
import { encodePublicPath } from '@/utils/encodePublicPath';

/** IBE WebP set under `/public/ibe_images/` (640 / 1080 / 1600). `url` is 1080 for non-book consumers. */
function ibeImage(
  id: string,
  folder: string,
  fileStem: string,
  alt: string,
  type: RoomImage['type'],
): RoomImage {
  const base = encodePublicPath(`/ibe_images/${folder}/${fileStem}`);
  return {
    id,
    url: `${base}.w1080.webp`,
    alt,
    type,
    width: 1080,
    height: 720,
    ibeSrc: {
      w640: `${base}.w640.webp`,
      w1080: `${base}.w1080.webp`,
      w1600: `${base}.w1600.webp`,
    },
  };
}

// Common amenities across all rooms
export const COMMON_AMENITIES: RoomAmenity[] = [
  {
    id: 'ensuite-bathroom',
    name: 'Ensuite Bathroom',
    description: 'Private bathroom with modern fixtures',
    icon: 'bath',
    category: 'comfort'
  },
  {
    id: 'luxury-bedding',
    name: 'Luxury Bedding',
    description: 'Premium Swiss bedding for ultimate comfort',
    icon: 'bed',
    category: 'comfort'
  },
  {
    id: 'flatscreen-tv',
    name: 'Flat Screen TV',
    description: 'High-definition television with cable channels',
    icon: 'tv',
    category: 'technology'
  },
  {
    id: 'wifi',
    name: 'High Speed Wi-Fi',
    description: 'High-speed wireless internet throughout the hotel',
    icon: 'wifi',
    category: 'technology'
  },
  {
    id: 'safe',
    name: 'In-Room Safe',
    description: 'Secure storage for valuables',
    icon: 'safe',
    category: 'comfort'
  },
  {
    id: 'hairdryer',
    name: 'Hair Dryer',
    description: 'Professional-grade hair dryer',
    icon: 'wind',
    category: 'comfort'
  }
];

// Room-specific amenities
export const BALCONY_AMENITY: RoomAmenity = {
  id: 'private-balcony',
  name: 'Private Balcony',
  description: 'Personal outdoor space with Alpine views',
  icon: 'mountain',
  category: 'view'
};

export const WELLNESS_AMENITY: RoomAmenity = {
  id: 'wellness-access',
  name: 'Wellness Access',
  description: 'Complimentary access to NutraSwiss wellness facilities',
  icon: 'spa',
  category: 'wellness'
};

export const KITCHENETTE_AMENITY: RoomAmenity = {
  id: 'kitchenette',
  name: 'Kitchenette',
  description: 'Compact kitchen facilities',
  icon: 'chef-hat',
  category: 'dining'
};

// Room data based on Content.md specifications
export const ROOMS_DATA: Room[] = [
  {
    id: 'basic-single',
    slug: 'basic-single',
    name: 'Basic Single',
    type: 'basic-single',
    category: 'classic',

    size: {
      squareMeters: 18,
      squareFeet: 194
    },
    capacity: {
      adults: 1,
      children: 0,
      maxOccupancy: 1
    },
    bedding: {
      type: 'single',
      configuration: '1 Single Bed'
    },

    view: 'standard',
    floor: 1,
    balcony: false,

    shortDescription: 'Comfortable and functional single room perfect for solo travelers',
    longDescription: 'Our Basic Single room offers essential comfort in a cozy 18m² space. Featuring modern amenities and a comfortable single bed, this room is ideal for business travelers or those seeking a simple, comfortable stay overlooking the beautiful Swiss landscape.',
    highlights: [
      'Cozy single accommodation',
      'Essential amenities',
      'Perfect for solo travelers',
      'Swiss comfort standards'
    ],
    uniqueFeatures: [
      'Compact yet comfortable design',
      'Modern bathroom facilities',
      'Peaceful location'
    ],

    images: [
      ibeImage(
        'basic-single-1',
        'Classic Single Room - Alpine Confort',
        'SingleComf 1',
        'Classic Single Room - Alpine Comfort with large window and comfortable bed',
        'hero',
      ),
      ibeImage(
        'basic-single-2',
        'Classic Single Room - Alpine Confort',
        'SingleComf 2',
        'Classic Single Room - Alpine Comfort with modern amenities',
        'gallery',
      ),
      ibeImage(
        'basic-single-3',
        'Classic Single Room - Alpine Confort',
        'SingleComf 3',
        'Classic Single Room - Alpine Comfort interior',
        'gallery',
      ),
      ibeImage(
        'basic-single-4',
        'Classic Single Room - Alpine Confort',
        'SingleComf 4',
        'Classic Single Room - Alpine Comfort detail',
        'gallery',
      ),
    ],

    amenities: COMMON_AMENITIES,
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 180,
      currency: 'CHF',
      seasonalPricing: {
        summer: 200,
        winter: 220,
        shoulder: 180
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  {
    id: 'standard-single',
    slug: 'standard-single',
    name: 'Classic Single Room - Alpine Comfort',
    type: 'standard-single',
    category: 'classic',

    size: {
      squareMeters: 20,
      squareFeet: 215
    },
    capacity: {
      adults: 1,
      children: 0,
      maxOccupancy: 1
    },
    bedding: {
      type: 'single',
      configuration: '1 Single Bed'
    },

    view: 'standard',
    floor: 2,
    balcony: false,

    shortDescription: 'Enhanced single room with premium amenities and extra space',
    longDescription: 'Our Standard Single room provides 20m² of comfortable space with enhanced amenities. Perfect for the discerning traveler seeking a bit more room and premium touches during their Swiss Alpine stay.',
    highlights: [
      'Enhanced single room',
      'Premium amenities',
      'Extra spacious comfort',
      'Alpine views available'
    ],
    uniqueFeatures: [
      'Larger room size',
      'Enhanced bathroom amenities',
      'Premium bedding'
    ],

    images: [
      {
        id: 'classic-single-1',
        url: '/images/optimized rooms/Classic Single Room - Alpine Comfort/SingleComf 1_pc.webp',
        alt: 'Classic Single Room - Alpine Comfort with large window and comfortable bed',
        type: 'hero',
        width: 1200,
        height: 800
      },
      {
        id: 'classic-single-2',
        url: '/images/optimized rooms/Classic Single Room - Alpine Comfort/SingleComf 2_pc.webp',
        alt: 'Classic Single Room - Alpine Comfort with modern amenities and cozy atmosphere',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'classic-single-3',
        url: '/images/optimized rooms/Classic Single Room - Alpine Comfort/SingleComf 3_pc.webp',
        alt: 'Classic Single Room - Alpine Comfort with elegant interior design',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'classic-single-4',
        url: '/images/optimized rooms/Classic Single Room - Alpine Comfort/SingleComf 4_pc.webp',
        alt: 'Classic Single Room - Alpine Comfort with premium bedding and comfort',
        type: 'gallery',
        width: 1200,
        height: 800
      }
    ],

    amenities: COMMON_AMENITIES,
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Enhanced breakfast service',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 220,
      currency: 'CHF',
      seasonalPricing: {
        summer: 240,
        winter: 260,
        shoulder: 220
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  {
    id: 'single-with-balcony',
    slug: 'single-with-balcony',
    name: 'Single with Balcony',
    type: 'single-with-balcony',
    category: 'lake-view',

    size: {
      squareMeters: 22,
      squareFeet: 237
    },
    capacity: {
      adults: 1,
      children: 0,
      maxOccupancy: 1
    },
    bedding: {
      type: 'single',
      configuration: '1 Single Bed'
    },

    view: 'lake',
    floor: 3,
    balcony: true,

    shortDescription: 'Single room with private balcony overlooking Lake Thun',
    longDescription: 'Enjoy breathtaking views of Lake Thun from your private balcony in our Single with Balcony room. This 22m² space combines comfort with spectacular Alpine scenery, perfect for those seeking a serene retreat.',
    highlights: [
      'Private balcony with lake views',
      'Breathtaking Alpine scenery',
      'Premium single accommodation',
      'Outdoor relaxation space'
    ],
    uniqueFeatures: [
      'Private balcony access',
      'Lake Thun views',
      'Outdoor seating area',
      'Fresh mountain air'
    ],

    images: [
      {
        id: 'single-balcony-1',
        url: '/images/Interiors/a_balcony_with_a_view_of_a_mountain_range_at_Parkh_162.webp',
        alt: 'Private balcony with stunning mountain range view',
        type: 'hero',
        width: 1200,
        height: 800
      },
      {
        id: 'single-balcony-2',
        url: '/images/Interiors/a_bedroom_with_a_sliding_glass_door_to_a_balcony_a_129.webp',
        alt: 'Bedroom with sliding glass door to balcony',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'single-balcony-3',
        url: '/images/Interiors/two_chairs_and_a_table_on_a_balcony_with_a_view_at_158.webp',
        alt: 'Balcony seating area with mountain views',
        type: 'gallery',
        width: 1200,
        height: 800
      }
    ],

    amenities: [...COMMON_AMENITIES, BALCONY_AMENITY],
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Enhanced breakfast service',
      'Balcony maintenance',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 280,
      currency: 'CHF',
      seasonalPricing: {
        summer: 320,
        winter: 300,
        shoulder: 280
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: true,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  {
    id: 'double-room',
    slug: 'double-room',
    name: 'Double Room',
    type: 'double-room',
    category: 'classic',

    size: {
      squareMeters: 25,
      squareFeet: 269
    },
    capacity: {
      adults: 2,
      children: 0,
      maxOccupancy: 2
    },
    bedding: {
      type: 'double',
      configuration: '1 Double Bed'
    },

    view: 'standard',
    floor: 1,
    balcony: false,

    shortDescription: 'Comfortable double room perfect for couples or business travelers',
    longDescription: 'Our Double Room offers 25m² of comfortable space with a king-size bed, ideal for couples or business travelers seeking quality accommodation in the Swiss Alps.',
    highlights: [
      'Comfortable double accommodation',
      'Flexible bedding options',
      'Perfect for couples',
      'Swiss comfort standards'
    ],
    uniqueFeatures: [
      'Spacious double room',
      'Comfortable king-size bed',
      'Flexible room configuration'
    ],

    images: [
      {
        id: 'double-room-1',
        url: '/images/Interiors/double rooms/double 1.webp',
        alt: 'Spacious double room with comfortable king-size bed',
        type: 'hero',
        width: 1200,
        height: 800
      },
      {
        id: 'double-room-2',
        url: '/images/Interiors/double rooms/Double 2.webp',
        alt: 'Double room with modern amenities and comfortable seating',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'double-room-3',
        url: '/images/Interiors/double rooms/double 3.webp',
        alt: 'Double room with elegant interior design',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'double-room-4',
        url: '/images/Interiors/double rooms/double 4.webp',
        alt: 'Double room with spacious layout and natural light',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'double-room-5',
        url: '/images/Interiors/double rooms/double 5.webp',
        alt: 'Double room with premium bedding and comfort',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'double-room-6',
        url: '/images/Interiors/double rooms/double 6.webp',
        alt: 'Double room with modern bathroom facilities',
        type: 'gallery',
        width: 1200,
        height: 800
      }
    ],

    amenities: COMMON_AMENITIES,
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Enhanced breakfast service',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 320,
      currency: 'CHF',
      seasonalPricing: {
        summer: 360,
        winter: 380,
        shoulder: 320
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  {
    id: 'double-room-with-balcony',
    slug: 'double-room-with-balcony',
    name: 'Double Room with Balcony',
    type: 'double-room-with-balcony',
    category: 'lake-view',

    size: {
      squareMeters: 28,
      squareFeet: 301
    },
    capacity: {
      adults: 2,
      children: 0,
      maxOccupancy: 2
    },
    bedding: {
      type: 'double',
      configuration: '1 Double Bed'
    },

    view: 'lake',
    floor: 2,
    balcony: true,

    shortDescription: 'Double room with private balcony and stunning lake views',
    longDescription: 'Experience luxury and breathtaking views in our Double Room with Balcony. This 28m² space features a comfortable king-size bed and a private balcony overlooking Lake Thun, perfect for couples seeking romance and relaxation.',
    highlights: [
      'Private balcony with lake views',
      'King-size bed comfort',
      'Romantic alpine retreat',
      'Outdoor relaxation space'
    ],
    uniqueFeatures: [
      'Private balcony access',
      'Lake Thun panorama',
      'Couples-focused amenities',
      'Alpine serenity'
    ],

    images: [
      {
        id: 'double-balcony-1',
        url: '/images/Interiors/a_bedroom_with_a_sliding_glass_door_to_a_balcony_a_129.webp',
        alt: 'Double room with sliding glass door to balcony',
        type: 'hero',
        width: 1200,
        height: 800
      },
      {
        id: 'double-balcony-2',
        url: '/images/Interiors/a_balcony_with_two_tables_and_a_view_of_mountains__38.webp',
        alt: 'Private balcony with outdoor seating and mountain views',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'double-balcony-3',
        url: '/images/Interiors/two_chairs_and_a_table_on_a_balcony_with_a_view_at_158.webp',
        alt: 'Balcony seating area with mountain views',
        type: 'gallery',
        width: 1200,
        height: 800
      }
    ],

    amenities: [...COMMON_AMENITIES, BALCONY_AMENITY],
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Enhanced breakfast service',
      'Balcony maintenance',
      'Romantic turndown service',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 380,
      currency: 'CHF',
      seasonalPricing: {
        summer: 420,
        winter: 450,
        shoulder: 380
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: true,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  {
    id: 'twin-room',
    slug: 'twin-room',
    name: 'Twin Room',
    type: 'twin-room',
    category: 'classic',

    size: {
      squareMeters: 25,
      squareFeet: 269
    },
    capacity: {
      adults: 2,
      children: 0,
      maxOccupancy: 2
    },
    bedding: {
      type: 'twin',
      configuration: '2 Single Beds'
    },

    view: 'standard',
    floor: 1,
    balcony: false,

    shortDescription: 'Comfortable twin room with two single beds',
    longDescription: 'Our Twin Room offers 25m² of comfortable space with two single beds, perfect for friends, colleagues, or family members traveling together who prefer separate sleeping arrangements.',
    highlights: [
      'Two comfortable single beds',
      'Flexible twin configuration',
      'Perfect for companions',
      'Swiss comfort standards'
    ],
    uniqueFeatures: [
      'Separate sleeping arrangements',
      'Flexible room layout',
      'Companion-friendly design'
    ],

    images: [
      {
        id: 'twin-room-1',
        url: '/images/Interiors/a_bedroom_with_two_beds_and_a_large_window_at_Park_206.webp',
        alt: 'Twin room with two single beds and large window',
        type: 'hero',
        width: 1200,
        height: 800
      },
      {
        id: 'twin-room-2',
        url: '/images/Interiors/two_beds_in_a_room_with_white_sheets_at_Parkhotel__201.webp',
        alt: 'Twin room with two comfortable beds',
        type: 'gallery',
        width: 1200,
        height: 800
      }
    ],

    amenities: COMMON_AMENITIES,
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Enhanced breakfast service',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 340,
      currency: 'CHF',
      seasonalPricing: {
        summer: 380,
        winter: 400,
        shoulder: 340
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  {
    id: 'twin-room-with-balcony',
    slug: 'twin-room-with-balcony',
    name: 'Twin Room with Balcony',
    type: 'twin-room-with-balcony',
    category: 'lake-view',

    size: {
      squareMeters: 28,
      squareFeet: 301
    },
    capacity: {
      adults: 2,
      children: 0,
      maxOccupancy: 2
    },
    bedding: {
      type: 'twin',
      configuration: '2 Single Beds'
    },

    view: 'lake',
    floor: 2,
    balcony: true,

    shortDescription: 'Twin room with private balcony and alpine views',
    longDescription: 'Our Twin Room with Balcony combines comfort with spectacular views. Featuring two single beds and a private balcony overlooking Lake Thun, this room is perfect for friends or family members seeking both comfort and stunning scenery.',
    highlights: [
      'Private balcony with lake views',
      'Two comfortable single beds',
      'Alpine scenery',
      'Flexible companion travel'
    ],
    uniqueFeatures: [
      'Private balcony access',
      'Separate sleeping arrangements',
      'Lake Thun views',
      'Companion-focused design'
    ],

    images: [
      {
        id: 'twin-balcony-1',
        url: '/images/Interiors/a_bedroom_with_two_beds_and_a_window_at_Parkhotel__186.webp',
        alt: 'Twin room with balcony access and mountain views',
        type: 'hero',
        width: 1200,
        height: 800
      },
      {
        id: 'twin-balcony-2',
        url: '/images/Interiors/a_room_with_two_beds_and_a_chair_in_it_at_Parkhote_64.webp',
        alt: 'Twin room interior with comfortable seating',
        type: 'gallery',
        width: 1200,
        height: 800
      }
    ],

    amenities: [...COMMON_AMENITIES, BALCONY_AMENITY],
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Enhanced breakfast service',
      'Balcony maintenance',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 400,
      currency: 'CHF',
      seasonalPricing: {
        summer: 450,
        winter: 480,
        shoulder: 400
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  {
    id: 'standard-triple-room',
    slug: 'standard-triple-room',
    name: 'Standard Triple Room',
    type: 'standard-triple-room',
    category: 'classic',

    size: {
      squareMeters: 30,
      squareFeet: 323
    },
    capacity: {
      adults: 3,
      children: 0,
      maxOccupancy: 3
    },
    bedding: {
      type: 'triple',
      configuration: '1 Double + 1 Single Bed or 3 Single Beds'
    },

    view: 'standard',
    floor: 1,
    balcony: true,

    shortDescription: 'Spacious triple room with balcony, perfect for families or small groups',
    longDescription: 'Our Standard Triple Room offers 30m² of comfortable space with flexible bedding options for up to 4 guests and a private balcony. Perfect for families or small groups seeking quality accommodation with Swiss comfort standards.',
    highlights: [
      'Spacious family accommodation',
      'Private balcony',
      'Flexible bedding for 3-4 guests',
      'Family-friendly amenities',
      'Comfortable group travel'
    ],
    uniqueFeatures: [
      'Flexible bedding configurations',
      'Private balcony access',
      'Family-focused design',
      'Extra space for comfort',
      'Group accommodation solution'
    ],

    images: [
      ibeImage(
        'triple-room-1',
        'Classic Triple Room - Alpine Comfort',
        'classic Triple 1',
        'Classic Triple Room - Alpine Comfort bedroom',
        'hero',
      ),
      ibeImage(
        'triple-room-2',
        'Classic Triple Room - Alpine Comfort',
        'classic Triple 2',
        'Classic Triple Room - Alpine Comfort interior',
        'gallery',
      ),
      ibeImage(
        'triple-room-3',
        'Classic Triple Room - Alpine Comfort',
        'classic triple 3',
        'Classic Triple Room - Alpine Comfort seating',
        'gallery',
      ),
      ibeImage(
        'triple-room-4',
        'Classic Triple Room - Alpine Comfort',
        'classic triple 4 ',
        'Classic Triple Room - Alpine Comfort layout',
        'gallery',
      ),
      ibeImage(
        'triple-room-5',
        'Classic Triple Room - Alpine Comfort',
        'classic triple 5',
        'Classic Triple Room - Alpine Comfort bathroom',
        'gallery',
      ),
      ibeImage(
        'triple-room-6',
        'Classic Triple Room - Alpine Comfort',
        'classic triple 6',
        'Classic Triple Room - Alpine Comfort view',
        'gallery',
      ),
    ],

    amenities: [...COMMON_AMENITIES, BALCONY_AMENITY],
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Enhanced breakfast service',
      'Family amenities',
      'Balcony maintenance',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 480,
      currency: 'CHF',
      seasonalPricing: {
        summer: 520,
        winter: 550,
        shoulder: 480
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  {
    id: 'superior-double-room',
    slug: 'superior-double-room',
    name: 'Superior Double Room',
    type: 'superior-double-room',
    category: 'classic',

    size: {
      squareMeters: 35,
      squareFeet: 377
    },
    capacity: {
      adults: 2,
      children: 0,
      maxOccupancy: 2
    },
    bedding: {
      type: 'double',
      configuration: '1 King Bed'
    },

    view: 'mountain',
    floor: 2,
    balcony: true,

    shortDescription: 'Enhanced double room with superior amenities and extra space',
    longDescription: 'Our Superior Double Room offers 35m² of luxurious space with enhanced amenities and a king-size bed. Featuring premium touches and extra comfort, this room provides an elevated experience for discerning guests.',
    highlights: [
      'Superior double accommodation',
      'Enhanced amenities and space',
      'Premium comfort features',
      'Elevated guest experience'
    ],
    uniqueFeatures: [
      'Larger room size',
      'Premium amenities',
      'Enhanced comfort features',
      'Superior service level'
    ],

    images: [
      ibeImage(
        'superior-double-1',
        'Jungfrau Superior Double - Balcony and Lake view',
        'JungSupDouble 1',
        'Jungfrau Superior Double - Balcony & Lake View bedroom',
        'hero',
      ),
      ibeImage(
        'superior-double-2',
        'Jungfrau Superior Double - Balcony and Lake view',
        'JungSupDouble 2',
        'Jungfrau Superior Double - Balcony & Lake View living area',
        'gallery',
      ),
      ibeImage(
        'superior-double-3',
        'Jungfrau Superior Double - Balcony and Lake view',
        'JungSupDouble 3',
        'Jungfrau Superior Double - Balcony & Lake View interior',
        'gallery',
      ),
      ibeImage(
        'superior-double-4',
        'Jungfrau Superior Double - Balcony and Lake view',
        'JungSupDouble 4',
        'Jungfrau Superior Double - Balcony & Lake View bathroom',
        'gallery',
      ),
      ibeImage(
        'superior-double-5',
        'Jungfrau Superior Double - Balcony and Lake view',
        'JungSupDouble 5',
        'Jungfrau Superior Double - Balcony & Lake View balcony',
        'gallery',
      ),
      ibeImage(
        'superior-double-6',
        'Jungfrau Superior Double - Balcony and Lake view',
        'JungSupDouble 6',
        'Jungfrau Superior Double - Balcony & Lake View view',
        'gallery',
      ),
      ibeImage(
        'superior-double-7',
        'Jungfrau Superior Double - Balcony and Lake view',
        'JungSupDouble 7',
        'Jungfrau Superior Double - Balcony & Lake View comfort',
        'gallery',
      ),
    ],

    amenities: [...COMMON_AMENITIES, WELLNESS_AMENITY],
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Enhanced breakfast service',
      'Wellness access',
      'Premium amenities',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 450,
      currency: 'CHF',
      seasonalPricing: {
        summer: 500,
        winter: 550,
        shoulder: 450
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  {
    id: 'alpine-double-twin-balcony-lake',
    slug: 'alpine-double-twin-balcony-lake',
    name: 'Alpine Double/Twin - Balcony & Lake View',
    type: 'alpine-double-twin-balcony-lake',
    category: 'lake-view',

    size: {
      squareMeters: 32,
      squareFeet: 344
    },
    capacity: {
      adults: 2,
      children: 0,
      maxOccupancy: 2
    },
    bedding: {
      type: 'flexible',
      configuration: '1 Double Bed or 2 Twin Beds'
    },

    view: 'lake',
    floor: 2,
    balcony: true,

    shortDescription: 'Flexible Alpine room with balcony and stunning lake views',
    longDescription: 'Our Alpine Double/Twin room offers 32m² of comfortable space with flexible bedding options and a private balcony overlooking Lake Thun. Perfect for couples or friends seeking both comfort and breathtaking Alpine scenery.',
    highlights: [
      'Flexible double or twin configuration',
      'Private balcony with lake views',
      'Stunning Alpine scenery',
      'Perfect for couples or friends'
    ],
    uniqueFeatures: [
      'Flexible bedding arrangements',
      'Private balcony access',
      'Lake Thun panorama',
      'Alpine comfort and style'
    ],

    images: [
      ibeImage(
        'alpine-double-1',
        'Alpine Double Twin- Balcony and Lake view',
        'Alp Double Balc 1',
        'Alpine Double/Twin - Balcony & Lake View bedroom',
        'hero',
      ),
      ibeImage(
        'alpine-double-2',
        'Alpine Double Twin- Balcony and Lake view',
        'Alp Double Balc 2',
        'Alpine Double/Twin - Balcony & Lake View interior',
        'gallery',
      ),
      ibeImage(
        'alpine-double-3',
        'Alpine Double Twin- Balcony and Lake view',
        'Alp Double Balc 3',
        'Alpine Double/Twin - Balcony & Lake View living area',
        'gallery',
      ),
      ibeImage(
        'alpine-double-4',
        'Alpine Double Twin- Balcony and Lake view',
        'Alp Double Balc 4',
        'Alpine Double/Twin - Balcony & Lake View bathroom',
        'gallery',
      ),
      ibeImage(
        'alpine-double-5',
        'Alpine Double Twin- Balcony and Lake view',
        'Alp Double Balc 5',
        'Alpine Double/Twin - Balcony & Lake View balcony',
        'gallery',
      ),
      ibeImage(
        'alpine-double-6',
        'Alpine Double Twin- Balcony and Lake view',
        'Alp Double Balc 6',
        'Alpine Double/Twin - Balcony & Lake View view',
        'gallery',
      ),
      ibeImage(
        'alpine-double-7',
        'Alpine Double Twin- Balcony and Lake view',
        'Alp Double Balc 7',
        'Alpine Double/Twin - Balcony & Lake View detail',
        'gallery',
      ),
    ],

    amenities: [...COMMON_AMENITIES, BALCONY_AMENITY],
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Enhanced breakfast service',
      'Balcony maintenance',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 420,
      currency: 'CHF',
      seasonalPricing: {
        summer: 480,
        winter: 500,
        shoulder: 420
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  {
    id: 'alpine-classic-double-twin-village',
    slug: 'alpine-classic-double-twin-village',
    name: 'Alpine Classic Double/Twin - Village View',
    type: 'alpine-classic-double-twin-village',
    category: 'classic',

    size: {
      squareMeters: 28,
      squareFeet: 301
    },
    capacity: {
      adults: 2,
      children: 0,
      maxOccupancy: 2
    },
    bedding: {
      type: 'flexible',
      configuration: '1 Double Bed or 2 Twin Beds'
    },

    view: 'village',
    floor: 2,
    balcony: false,

    shortDescription: 'Classic Alpine room with charming village views and flexible bedding',
    longDescription: 'Our Alpine Classic Double/Twin room offers 28m² of comfortable space with flexible bedding options and peaceful village views. Perfect for couples or friends seeking authentic Alpine charm with the flexibility of double or twin bed configurations.',
    highlights: [
      'Flexible double or twin configuration',
      'Charming village views',
      'Classic Alpine atmosphere',
      'Perfect for couples or friends'
    ],
    uniqueFeatures: [
      'Flexible bedding arrangements',
      'Peaceful village setting',
      'Classic Alpine charm',
      'Authentic Swiss hospitality'
    ],

    images: [
      {
        id: 'alpine-classic-twin-1',
        url: '/images/optimized rooms/Alpine Classic Double Twin - Village View/AlpClaTwin 1_pc.webp',
        alt: 'Alpine Classic Twin - Village View bedroom',
        type: 'hero',
        width: 1200,
        height: 800
      },
      {
        id: 'alpine-classic-twin-2',
        url: '/images/optimized rooms/Alpine Classic Double Twin - Village View/AlpClaTwin 2_pc.webp',
        alt: 'Alpine Classic Twin - Village View interior',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'alpine-classic-twin-3',
        url: '/images/optimized rooms/Alpine Classic Double Twin - Village View/AlpClaTwin 3_pc.webp',
        alt: 'Alpine Classic Twin - Village View living area',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'alpine-classic-twin-4',
        url: '/images/optimized rooms/Alpine Classic Double Twin - Village View/AlpClaTwin 4_pc.webp',
        alt: 'Alpine Classic Twin - Village View bathroom',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'alpine-classic-twin-5',
        url: '/images/optimized rooms/Alpine Classic Double Twin - Village View/AlpClaTwin 5_pc.webp',
        alt: 'Alpine Classic Twin - Village View view',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'alpine-classic-twin-6',
        url: '/images/optimized rooms/Alpine Classic Double Twin - Village View/AlpClaTwin 6_pc.webp',
        alt: 'Alpine Classic Twin - Village View comfort',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'alpine-classic-twin-7',
        url: '/images/optimized rooms/Alpine Classic Double Twin - Village View/AlpClaTwin7_pc.webp',
        alt: 'Alpine Classic Twin - Village View design',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'alpine-classic-double-1',
        url: '/images/optimized rooms/Alpine Classic Double Twin - Village View/AlpClDouBal 1_pc.webp',
        alt: 'Alpine Classic Double - Village View bedroom',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'alpine-classic-double-2',
        url: '/images/optimized rooms/Alpine Classic Double Twin - Village View/AlpClDouBal 2_pc.webp',
        alt: 'Alpine Classic Double - Village View interior',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'alpine-classic-double-3',
        url: '/images/optimized rooms/Alpine Classic Double Twin - Village View/AlpClDouBal 3_pc.webp',
        alt: 'Alpine Classic Double - Village View living area',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'alpine-classic-double-4',
        url: '/images/optimized rooms/Alpine Classic Double Twin - Village View/AlpClDouBal 4_pc.webp',
        alt: 'Alpine Classic Double - Village View bathroom',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'alpine-classic-double-5',
        url: '/images/optimized rooms/Alpine Classic Double Twin - Village View/AlpClDouBal 5_pc.webp',
        alt: 'Alpine Classic Double - Village View view',
        type: 'gallery',
        width: 1200,
        height: 800
      },
      {
        id: 'alpine-classic-double-6',
        url: '/images/optimized rooms/Alpine Classic Double Twin - Village View/AlpClDouBal 6_pc.webp',
        alt: 'Alpine Classic Double - Village View comfort',
        type: 'gallery',
        width: 1200,
        height: 800
      }
    ],

    amenities: COMMON_AMENITIES,
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Enhanced breakfast service',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 380,
      currency: 'CHF',
      seasonalPricing: {
        summer: 420,
        winter: 450,
        shoulder: 380
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  {
    id: 'jungfrau-single-balcony-lake',
    slug: 'jungfrau-single-balcony-lake',
    name: 'Jungfrau Single - Balcony & Lake View',
    type: 'jungfrau-single-balcony-lake',
    category: 'lake-view',

    size: {
      squareMeters: 20,
      squareFeet: 215
    },
    capacity: {
      adults: 1,
      children: 0,
      maxOccupancy: 1
    },
    bedding: {
      type: 'single',
      configuration: '1 Single Bed'
    },

    view: 'lake',
    floor: 2,
    balcony: true,

    shortDescription: 'Single room with private balcony and stunning lake views',
    longDescription: 'Our Jungfrau Single room offers 20m² of comfortable space with a private balcony overlooking Lake Thun. Perfect for solo travelers seeking both comfort and breathtaking Alpine scenery with direct access to fresh mountain air.',
    highlights: [
      'Private balcony with lake views',
      'Breathtaking Alpine scenery',
      'Perfect for solo travelers',
      'Fresh mountain air access'
    ],
    uniqueFeatures: [
      'Private balcony access',
      'Lake Thun views',
      'Outdoor relaxation space',
      'Premium single accommodation'
    ],

    images: [
      ibeImage(
        'jungfrau-single-1',
        'Jungfrau Single - Balcony and Lake View',
        'JungSingle 1',
        'Jungfrau Single - Balcony & Lake View bedroom',
        'hero',
      ),
      ibeImage(
        'jungfrau-single-2',
        'Jungfrau Single - Balcony and Lake View',
        'JungSingle 2',
        'Jungfrau Single - Balcony & Lake View interior',
        'gallery',
      ),
      ibeImage(
        'jungfrau-single-3',
        'Jungfrau Single - Balcony and Lake View',
        'JungSingle 3',
        'Jungfrau Single - Balcony & Lake View living area',
        'gallery',
      ),
      ibeImage(
        'jungfrau-single-4',
        'Jungfrau Single - Balcony and Lake View',
        'JungSingle 4',
        'Jungfrau Single - Balcony & Lake View bathroom',
        'gallery',
      ),
      ibeImage(
        'jungfrau-single-5',
        'Jungfrau Single - Balcony and Lake View',
        'JungSingle 5',
        'Jungfrau Single - Balcony & Lake View view',
        'gallery',
      ),
    ],

    amenities: [...COMMON_AMENITIES, BALCONY_AMENITY],
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Enhanced breakfast service',
      'Balcony maintenance',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 220,
      currency: 'CHF',
      seasonalPricing: {
        summer: 240,
        winter: 260,
        shoulder: 220
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  {
    id: 'heritage-single-village',
    slug: 'heritage-single-village',
    name: 'Heritage Single - Village View',
    type: 'heritage-single-village',
    category: 'classic',

    size: {
      squareMeters: 20,
      squareFeet: 215
    },
    capacity: {
      adults: 1,
      children: 0,
      maxOccupancy: 1
    },
    bedding: {
      type: 'single',
      configuration: '1 Single Bed'
    },

    view: 'village',
    floor: 2,
    balcony: false,

    shortDescription: 'Classic single room with charming village views and heritage character',
    longDescription: 'Our Heritage Single room offers 20m² of comfortable space with peaceful village views. Perfect for solo travelers seeking authentic Alpine charm with classic Swiss hospitality and traditional character.',
    highlights: [
      'Charming village views',
      'Classic Alpine atmosphere',
      'Perfect for solo travelers',
      'Authentic Swiss heritage'
    ],
    uniqueFeatures: [
      'Peaceful village setting',
      'Classic Alpine charm',
      'Authentic Swiss hospitality',
      'Heritage character'
    ],

    images: [
      ibeImage(
        'heritage-single-1',
        'Heritage Single - Village View',
        'hs 1',
        'Heritage Single - Village View bedroom',
        'hero',
      ),
      ibeImage(
        'heritage-single-2',
        'Heritage Single - Village View',
        'hs 2',
        'Heritage Single - Village View interior',
        'gallery',
      ),
      ibeImage(
        'heritage-single-3',
        'Heritage Single - Village View',
        'hs 3',
        'Heritage Single - Village View living area',
        'gallery',
      ),
      ibeImage(
        'heritage-single-4',
        'Heritage Single - Village View',
        'HS bathroom 2',
        'Heritage Single - Village View bathroom',
        'gallery',
      ),
    ],

    amenities: COMMON_AMENITIES,
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Enhanced breakfast service',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 220,
      currency: 'CHF',
      seasonalPricing: {
        summer: 240,
        winter: 260,
        shoulder: 220
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  {
    id: 'junior-suite',
    slug: 'junior-suite',
    name: 'Junior Suite',
    type: 'junior-suite',
    category: 'suite',

    size: {
      squareMeters: 50,
      squareFeet: 538
    },
    capacity: {
      adults: 2,
      children: 0,
      maxOccupancy: 2
    },
    bedding: {
      type: 'double',
      configuration: '1 King Bed'
    },

    view: 'mountain',
    floor: 3,
    balcony: true,

    shortDescription: 'Luxurious junior suite with separate living area and mountain views',
    longDescription: 'Our Junior Suite offers 50m² of luxurious space with a separate living area, king-size bedroom, and breathtaking mountain views. This sophisticated accommodation combines comfort, style, and stunning Alpine scenery for an unforgettable stay.',
    highlights: [
      'Separate living and bedroom areas',
      'Mountain and lake views',
      'Luxurious suite experience',
      'Premium amenities throughout'
    ],
    uniqueFeatures: [
      'Separate living area',
      'Panoramic mountain views',
      'Suite-level service',
      'Enhanced privacy'
    ],

    images: [
      ibeImage(
        'junior-suite-1',
        'Alpine Juniour Suite - Panoroma View',
        'Alp Jun Sui 1',
        'Alpine Junior Suite - Panorama View bedroom',
        'hero',
      ),
      ibeImage(
        'junior-suite-2',
        'Alpine Juniour Suite - Panoroma View',
        'Alp Jun Sui 2',
        'Alpine Junior Suite - Panorama View living area',
        'gallery',
      ),
      ibeImage(
        'junior-suite-3',
        'Alpine Juniour Suite - Panoroma View',
        'Alp Jun Sui 3',
        'Alpine Junior Suite - Panorama View interior',
        'gallery',
      ),
      ibeImage(
        'junior-suite-4',
        'Alpine Juniour Suite - Panoroma View',
        'Alp Jun Sui 4',
        'Alpine Junior Suite - Panorama View bathroom',
        'gallery',
      ),
      ibeImage(
        'junior-suite-5',
        'Alpine Juniour Suite - Panoroma View',
        'Alp Jun Sui 5',
        'Alpine Junior Suite - Panorama View view',
        'gallery',
      ),
      ibeImage(
        'junior-suite-6',
        'Alpine Juniour Suite - Panoroma View',
        'Alp Jun Sui 6',
        'Alpine Junior Suite - Panorama View comfort',
        'gallery',
      ),
    ],

    amenities: [...COMMON_AMENITIES, BALCONY_AMENITY, WELLNESS_AMENITY],
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Enhanced breakfast service',
      'Wellness access',
      'Suite-level concierge',
      'Premium amenities',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 650,
      currency: 'CHF',
      seasonalPricing: {
        summer: 750,
        winter: 800,
        shoulder: 650
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: true,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  {
    id: 'family-suite',
    slug: 'family-suite',
    name: 'Family Suite',
    type: 'family-suite',
    category: 'suite',

    size: {
      squareMeters: 65,
      squareFeet: 700
    },
    capacity: {
      adults: 4,
      children: 2,
      maxOccupancy: 6
    },
    bedding: {
      type: 'flexible',
      configuration: '3 Double Beds + Sofa Bed'
    },

    view: 'mountain',
    floor: 3,
    balcony: true,

    shortDescription: 'Spacious family suite with panoramic views and kitchenette',
    longDescription: 'Our Family Suite offers 65m² of luxurious space designed for families and groups. Featuring three double beds, a kitchenette, and panoramic views of Lake Thun and the Bernese Alps, this suite provides the perfect home-away-from-home experience.',
    highlights: [
      'Spacious family accommodation',
      'Panoramic lake and mountain views',
      'Kitchenette for added convenience',
      'Multiple sleeping areas'
    ],
    uniqueFeatures: [
      'Kitchenette facilities',
      'Panoramic views',
      'Multiple sleeping configurations',
      'Family-focused amenities'
    ],

    images: [
      ibeImage(
        'family-suite-1',
        'Heritage Family Suite - Jungfrau View',
        'HFS 1',
        'Heritage Family Suite - Jungfrau View main bedroom',
        'hero',
      ),
      ibeImage(
        'family-suite-2',
        'Heritage Family Suite - Jungfrau View',
        'HFS 2',
        'Heritage Family Suite - Jungfrau View living area',
        'gallery',
      ),
      ibeImage(
        'family-suite-3',
        'Heritage Family Suite - Jungfrau View',
        'HFS 3',
        'Heritage Family Suite - Jungfrau View bedroom',
        'gallery',
      ),
      ibeImage(
        'family-suite-4',
        'Heritage Family Suite - Jungfrau View',
        'HFS 4',
        'Heritage Family Suite - Jungfrau View bathroom',
        'gallery',
      ),
      ibeImage(
        'family-suite-5',
        'Heritage Family Suite - Jungfrau View',
        'HFS 5',
        'Heritage Family Suite - Jungfrau View balcony',
        'gallery',
      ),
      ibeImage(
        'family-suite-6',
        'Heritage Family Suite - Jungfrau View',
        'HFS 6',
        'Heritage Family Suite - Jungfrau View interior',
        'gallery',
      ),
      ibeImage(
        'family-suite-7',
        'Heritage Family Suite - Jungfrau View',
        'HFS 7',
        'Heritage Family Suite - Jungfrau View view',
        'gallery',
      ),
      ibeImage(
        'family-suite-8',
        'Heritage Family Suite - Jungfrau View',
        'HFS 8',
        'Heritage Family Suite - Jungfrau View space',
        'gallery',
      ),
      ibeImage(
        'family-suite-9',
        'Heritage Family Suite - Jungfrau View',
        'HFS 9',
        'Heritage Family Suite - Jungfrau View comfort',
        'gallery',
      ),
      ibeImage(
        'family-suite-10',
        'Heritage Family Suite - Jungfrau View',
        'HFS 10',
        'Heritage Family Suite - Jungfrau View amenities',
        'gallery',
      ),
      ibeImage(
        'family-suite-11',
        'Heritage Family Suite - Jungfrau View',
        'HFS 11',
        'Heritage Family Suite - Jungfrau View design',
        'gallery',
      ),
      ibeImage(
        'family-suite-12',
        'Heritage Family Suite - Jungfrau View',
        'HFS 12',
        'Heritage Family Suite - Jungfrau View luxury',
        'gallery',
      ),
    ],

    amenities: [...COMMON_AMENITIES, BALCONY_AMENITY, KITCHENETTE_AMENITY],
    includedServices: [
      'Daily housekeeping',
      '24-hour reception',
      'High Speed Wi-Fi',
      'Enhanced breakfast service',
      'Kitchenette supplies',
      'Family amenities',
      'Access to hotel facilities'
    ],

    pricing: {
      basePrice: 850,
      currency: 'CHF',
      seasonalPricing: {
        summer: 950,
        winter: 1000,
        shoulder: 850
      },
      discounts: {
        earlyBird: 10,
        longStay: 15,
        wellnessPackage: 20
      }
    },

    featured: true,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },

  // --- MEWS ladder aliases (same content as originals, local ids for 10 MEWS room categories) ---
  {
    id: 'double-twin-balcony-lake',
    slug: 'double-twin-balcony-lake',
    name: 'Alpine Double/Twin - Balcony & Lake View',
    type: 'double-twin-balcony-lake',
    category: 'lake-view',

    size: { squareMeters: 32, squareFeet: 344 },
    capacity: { adults: 2, children: 0, maxOccupancy: 2 },
    bedding: { type: 'flexible', configuration: '1 Double Bed or 2 Twin Beds' },

    view: 'mountain',
    floor: 2,
    balcony: true,

    shortDescription: 'Flexible Alpine room with balcony and stunning lake views',
    longDescription: 'Our Alpine Double/Twin room offers 32m² of comfortable space with flexible bedding options and a private balcony overlooking Lake Thun. Perfect for couples or friends seeking both comfort and breathtaking Alpine scenery.',
    highlights: ['Flexible double or twin configuration', 'Private balcony with lake views', 'Stunning Alpine scenery', 'Perfect for couples or friends'],
    uniqueFeatures: ['Flexible bedding arrangements', 'Private balcony access', 'Lake Thun panorama', 'Alpine comfort and style'],

    images: [
      ibeImage('alpine-double-1', 'Alpine Double Twin- Balcony and Lake view', 'Alp Double Balc 1', 'Alpine Double/Twin - Balcony & Lake View bedroom', 'hero'),
      ibeImage('alpine-double-2', 'Alpine Double Twin- Balcony and Lake view', 'Alp Double Balc 2', 'Alpine Double/Twin - Balcony & Lake View interior', 'gallery'),
      ibeImage('alpine-double-3', 'Alpine Double Twin- Balcony and Lake view', 'Alp Double Balc 3', 'Alpine Double/Twin - Balcony & Lake View living area', 'gallery'),
      ibeImage('alpine-double-4', 'Alpine Double Twin- Balcony and Lake view', 'Alp Double Balc 4', 'Alpine Double/Twin - Balcony & Lake View bathroom', 'gallery'),
      ibeImage('alpine-double-5', 'Alpine Double Twin- Balcony and Lake view', 'Alp Double Balc 5', 'Alpine Double/Twin - Balcony & Lake View balcony', 'gallery'),
      ibeImage('alpine-double-6', 'Alpine Double Twin- Balcony and Lake view', 'Alp Double Balc 6', 'Alpine Double/Twin - Balcony & Lake View view', 'gallery'),
      ibeImage('alpine-double-7', 'Alpine Double Twin- Balcony and Lake view', 'Alp Double Balc 7', 'Alpine Double/Twin - Balcony & Lake View detail', 'gallery'),
    ],

    amenities: [...COMMON_AMENITIES, BALCONY_AMENITY],
    includedServices: ['Daily housekeeping', '24-hour reception', 'High Speed Wi-Fi', 'Enhanced breakfast service', 'Balcony maintenance', 'Access to hotel facilities'],
    pricing: { basePrice: 420, currency: 'CHF', seasonalPricing: { summer: 480, winter: 500, shoulder: 420 }, discounts: { earlyBird: 10, longStay: 15, wellnessPackage: 20 } },
    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'double-twin-balcony-village',
    slug: 'double-twin-balcony-village',
    name: 'Double Room with Balcony',
    type: 'double-twin-balcony-village',
    category: 'lake-view',

    size: { squareMeters: 28, squareFeet: 301 },
    capacity: { adults: 2, children: 0, maxOccupancy: 2 },
    bedding: { type: 'double', configuration: '1 Double Bed' },

    view: 'lake',
    floor: 2,
    balcony: true,

    shortDescription: 'Double room with private balcony and stunning lake views',
    longDescription: 'Experience luxury and breathtaking views in our Double Room with Balcony. This 28m² space features a comfortable king-size bed and a private balcony overlooking Lake Thun, perfect for couples seeking romance and relaxation.',
    highlights: ['Private balcony with lake views', 'King-size bed comfort', 'Romantic alpine retreat', 'Outdoor relaxation space'],
    uniqueFeatures: ['Private balcony access', 'Lake Thun panorama', 'Couples-focused amenities', 'Alpine serenity'],

    images: [
      ibeImage(
        'double-balcony-1',
        'Alpine Classic Double Twin - Village View Balcony',
        'AlpClDouBal 1',
        'Alpine Double/Twin - Village View Balcony bedroom',
        'hero',
      ),
      ibeImage(
        'double-balcony-2',
        'Alpine Classic Double Twin - Village View Balcony',
        'AlpClDouBal 2',
        'Alpine Double/Twin - Village View Balcony interior',
        'gallery',
      ),
      ibeImage(
        'double-balcony-3',
        'Alpine Classic Double Twin - Village View Balcony',
        'AlpClDouBal 3',
        'Alpine Double/Twin - Village View Balcony living area',
        'gallery',
      ),
      ibeImage(
        'double-balcony-4',
        'Alpine Classic Double Twin - Village View Balcony',
        'AlpClDouBal 4',
        'Alpine Double/Twin - Village View Balcony bathroom',
        'gallery',
      ),
      ibeImage(
        'double-balcony-5',
        'Alpine Classic Double Twin - Village View Balcony',
        'AlpClDouBal 5',
        'Alpine Double/Twin - Village View Balcony balcony',
        'gallery',
      ),
      ibeImage(
        'double-balcony-6',
        'Alpine Classic Double Twin - Village View Balcony',
        'AlpClDouBal 6',
        'Alpine Double/Twin - Village View Balcony view',
        'gallery',
      ),
    ],

    amenities: [...COMMON_AMENITIES, BALCONY_AMENITY],
    includedServices: ['Daily housekeeping', '24-hour reception', 'High Speed Wi-Fi', 'Enhanced breakfast service', 'Balcony maintenance', 'Romantic turndown service', 'Access to hotel facilities'],
    pricing: { basePrice: 380, currency: 'CHF', seasonalPricing: { summer: 420, winter: 450, shoulder: 380 }, discounts: { earlyBird: 10, longStay: 15, wellnessPackage: 20 } },
    featured: true,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'double-twin-noBal',
    slug: 'double-twin-noBal',
    name: 'Double Room',
    type: 'double-twin-noBal',
    category: 'classic',

    size: { squareMeters: 25, squareFeet: 269 },
    capacity: { adults: 2, children: 0, maxOccupancy: 2 },
    bedding: { type: 'double', configuration: '1 Double Bed' },

    view: 'standard',
    floor: 1,
    balcony: false,

    shortDescription: 'Comfortable double room perfect for couples or business travelers',
    longDescription: 'Our Double Room offers 25m² of comfortable space with a king-size bed, ideal for couples or business travelers seeking quality accommodation in the Swiss Alps.',
    highlights: ['Comfortable double accommodation', 'Flexible bedding options', 'Perfect for couples', 'Swiss comfort standards'],
    uniqueFeatures: ['Spacious double room', 'Comfortable king-size bed', 'Flexible room configuration'],

    images: [
      ibeImage(
        'double-room-1',
        'Alpine Classic Double Twin - Without Balcony',
        'AlpClaTwin 1',
        'Classic Double/Twin without balcony bedroom',
        'hero',
      ),
      ibeImage(
        'double-room-2',
        'Alpine Classic Double Twin - Without Balcony',
        'AlpClaTwin 2',
        'Classic Double/Twin without balcony interior',
        'gallery',
      ),
      ibeImage(
        'double-room-3',
        'Alpine Classic Double Twin - Without Balcony',
        'AlpClaTwin 3',
        'Classic Double/Twin without balcony living area',
        'gallery',
      ),
      ibeImage(
        'double-room-4',
        'Alpine Classic Double Twin - Without Balcony',
        'AlpClaTwin 4',
        'Classic Double/Twin without balcony bathroom',
        'gallery',
      ),
      ibeImage(
        'double-room-5',
        'Alpine Classic Double Twin - Without Balcony',
        'AlpClaTwin 5',
        'Classic Double/Twin without balcony detail',
        'gallery',
      ),
      ibeImage(
        'double-room-6',
        'Alpine Classic Double Twin - Without Balcony',
        'AlpClaTwin 6',
        'Classic Double/Twin without balcony view',
        'gallery',
      ),
      ibeImage(
        'double-room-7',
        'Alpine Classic Double Twin - Without Balcony',
        'AlpClaTwin 7',
        'Classic Double/Twin without balcony comfort',
        'gallery',
      ),
    ],

    amenities: COMMON_AMENITIES,
    includedServices: ['Daily housekeeping', '24-hour reception', 'High Speed Wi-Fi', 'Enhanced breakfast service', 'Access to hotel facilities'],
    pricing: { basePrice: 320, currency: 'CHF', seasonalPricing: { summer: 360, winter: 380, shoulder: 320 }, discounts: { earlyBird: 10, longStay: 15, wellnessPackage: 20 } },
    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'single-balcony-lake',
    slug: 'single-balcony-lake',
    name: 'Jungfrau Single - Balcony & Lake View',
    type: 'single-balcony-lake',
    category: 'lake-view',

    size: { squareMeters: 20, squareFeet: 215 },
    capacity: { adults: 1, children: 0, maxOccupancy: 1 },
    bedding: { type: 'single', configuration: '1 Single Bed' },

    view: 'mountain',
    floor: 2,
    balcony: true,

    shortDescription: 'Single room with private balcony and stunning lake views',
    longDescription: 'Our Jungfrau Single room offers 20m² of comfortable space with a private balcony overlooking Lake Thun. Perfect for solo travelers seeking both comfort and breathtaking Alpine scenery with direct access to fresh mountain air.',
    highlights: ['Private balcony with lake views', 'Breathtaking Alpine scenery', 'Perfect for solo travelers', 'Fresh mountain air access'],
    uniqueFeatures: ['Private balcony access', 'Lake Thun views', 'Outdoor relaxation space', 'Premium single accommodation'],

    images: [
      ibeImage('jungfrau-single-1', 'Jungfrau Single - Balcony and Lake View', 'JungSingle 1', 'Jungfrau Single - Balcony & Lake View bedroom', 'hero'),
      ibeImage('jungfrau-single-2', 'Jungfrau Single - Balcony and Lake View', 'JungSingle 2', 'Jungfrau Single - Balcony & Lake View interior', 'gallery'),
      ibeImage('jungfrau-single-3', 'Jungfrau Single - Balcony and Lake View', 'JungSingle 3', 'Jungfrau Single - Balcony & Lake View living area', 'gallery'),
      ibeImage('jungfrau-single-4', 'Jungfrau Single - Balcony and Lake View', 'JungSingle 4', 'Jungfrau Single - Balcony & Lake View bathroom', 'gallery'),
      ibeImage('jungfrau-single-5', 'Jungfrau Single - Balcony and Lake View', 'JungSingle 5', 'Jungfrau Single - Balcony & Lake View view', 'gallery'),
    ],

    amenities: [...COMMON_AMENITIES, BALCONY_AMENITY],
    includedServices: ['Daily housekeeping', '24-hour reception', 'High Speed Wi-Fi', 'Enhanced breakfast service', 'Balcony maintenance', 'Access to hotel facilities'],
    pricing: { basePrice: 220, currency: 'CHF', seasonalPricing: { summer: 240, winter: 260, shoulder: 220 }, discounts: { earlyBird: 10, longStay: 15, wellnessPackage: 20 } },
    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'heritage-single',
    slug: 'heritage-single',
    name: 'Classic Single Room - Alpine Comfort',
    type: 'heritage-single',
    category: 'classic',

    size: { squareMeters: 20, squareFeet: 215 },
    capacity: { adults: 1, children: 0, maxOccupancy: 1 },
    bedding: { type: 'single', configuration: '1 Single Bed' },

    view: 'standard',
    floor: 2,
    balcony: false,

    shortDescription: 'Enhanced single room with premium amenities and extra space',
    longDescription: 'Our Standard Single room provides 20m² of comfortable space with enhanced amenities. Perfect for the discerning traveler seeking a bit more room and premium touches during their Swiss Alpine stay.',
    highlights: ['Enhanced single room', 'Premium amenities', 'Extra spacious comfort', 'Alpine views available'],
    uniqueFeatures: ['Larger room size', 'Enhanced bathroom amenities', 'Premium bedding'],

    images: [
      ibeImage(
        'heritage-mews-1',
        'Heritage Single - Village View',
        'hs 1',
        'Heritage Single - Village View bedroom',
        'hero',
      ),
      ibeImage(
        'heritage-mews-2',
        'Heritage Single - Village View',
        'hs 2',
        'Heritage Single - Village View interior',
        'gallery',
      ),
      ibeImage(
        'heritage-mews-3',
        'Heritage Single - Village View',
        'hs 3',
        'Heritage Single - Village View living area',
        'gallery',
      ),
      ibeImage(
        'heritage-mews-4',
        'Heritage Single - Village View',
        'HS bathroom 2',
        'Heritage Single - Village View bathroom',
        'gallery',
      ),
    ],

    amenities: COMMON_AMENITIES,
    includedServices: ['Daily housekeeping', '24-hour reception', 'High Speed Wi-Fi', 'Enhanced breakfast service', 'Access to hotel facilities'],
    pricing: { basePrice: 220, currency: 'CHF', seasonalPricing: { summer: 240, winter: 260, shoulder: 220 }, discounts: { earlyBird: 10, longStay: 15, wellnessPackage: 20 } },
    featured: false,
    available: true,
    seasonalAvailability: 'year-round',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  }
];

// Helper functions
export const getRoomById = (id: string): Room | undefined => {
  return ROOMS_DATA.find(room => room.id === id);
};

export const getRoomBySlug = (slug: string): Room | undefined => {
  return ROOMS_DATA.find(room => room.slug === slug);
};

export const getRoomsByCategory = (category: Room['category']): Room[] => {
  return ROOMS_DATA.filter(room => room.category === category);
};

export const getFeaturedRooms = (): Room[] => {
  return ROOMS_DATA.filter(room => room.featured);
};

export const getRoomsByType = (type: RoomType): Room[] => {
  return ROOMS_DATA.filter(room => room.type === type);
};

export const getRoomsByView = (view: ViewType): Room[] => {
  return ROOMS_DATA.filter(room => room.view === view);
};

export const getRoomsWithBalcony = (): Room[] => {
  return ROOMS_DATA.filter(room => room.balcony);
};
